/**
 * Designed  & Developed by Faisal Nadaf
 * Contact: faisalfnadaf@gmail.com
 *
 * @format
 */

import { Navigation } from "@/components/customCopmonent/landingSection/navigation";
import { FooterSection } from "@/components/customCopmonent/landingSection/footer-section";
import { CtaSection } from "@/components/customCopmonent/landingSection/cta-section";
import DotGrid from "@/components/customCopmonent/DotGrid";
import {
	PageHeaderHero,
	PAGE_HERO_CLASS,
} from "@/components/customCopmonent/Text/PageHeaderHero";
import { SectionWrapper } from "../Clientele.page-primitives";
import Seo from "@/components/Seo";
import AcheShowcase, {
	type ShowcaseIcon,
	type ShowcaseHighlight,
	type ShowcaseSpec,
} from "@/components/customCopmonent/AcheShowcase";
import { useTranslation } from "react-i18next";

/**
 * Everything that varies between one product detail page and the next.
 *
 * All copy lives in `src/i18n/locales/<lang>/products.json` under
 * `productPages.<i18nKey>`, so a page definition only carries its keys, its
 * artwork and the icon set that decorates the highlight rows and spec table.
 */
export interface ProductDetailConfig {
	/** Key under `productPages` in the products i18n namespace. */
	i18nKey: string;
	/** Route this page is mounted at — used for hreflang alternates. */
	path: string;
	/** Photo behind the blue hero band. */
	heroBackground: string;
	/** Product renders shown in the showcase card (first one is the default). */
	images: string[];
	/** Sub-keys of `showcase.highlights`, paired positionally with the icons. */
	highlightKeys: readonly string[];
	highlightIcons: readonly ShowcaseIcon[];
	/** Sub-keys of `showcase.specs`, paired positionally with the icons. */
	specKeys: readonly string[];
	specIcons: readonly ShowcaseIcon[];
	/**
	 * Decorative captions on the showcase card. The current AcheShowcase build
	 * keeps its card stripped back to the render alone and paints none of them,
	 * but every other product page supplies them, so these stay in step.
	 */
	wordmark: string;
	compliancePill?: string;
	captionEyebrow?: string;
	captionTitle?: string;
	specsFooterLabel?: string;
}

/**
 * Shared shell for a single product: SEO head, blue hero band, the engineering
 * showcase (overview + key features + specifications table), enquiry CTA and
 * the site footer — in the same order and with the same primitives as the rest
 * of the product catalogue.
 */
export default function ProductDetailPage({
	config,
}: {
	config: ProductDetailConfig;
}) {
	const { t } = useTranslation();
	const base = `productPages.${config.i18nKey}`;

	const highlights: ShowcaseHighlight[] = config.highlightKeys.map((key, i) => ({
		icon: config.highlightIcons[i],
		title: t(`${base}.showcase.highlights.${key}.title`),
		body: t(`${base}.showcase.highlights.${key}.body`),
	}));

	const specs: ShowcaseSpec[] = config.specKeys.map((key, i) => ({
		icon: config.specIcons[i],
		feature: t(`${base}.showcase.specs.${key}.feature`),
		capability: t(`${base}.showcase.specs.${key}.capability`),
	}));

	// The lead sentence names the company; pull that fragment out so it can be
	// emphasised inline, exactly as the existing product pages do.
	const company = t(`${base}.showcase.descriptionLeadCompany`);
	const descriptionParts = t(`${base}.showcase.description`, {
		company,
	}).split(company);

	return (
		<>
			<Seo
				titleKey={`${base}.seo.title`}
				descriptionKey={`${base}.seo.description`}
				path={config.path}
			/>
			<Navigation />

			{/* HERO */}
			<SectionWrapper className={PAGE_HERO_CLASS}>
				<div className="absolute inset-0">
					<div
						className="absolute bottom-3 inset-0 opacity-35 scale-105"
						style={{
							backgroundImage: `url(${config.heroBackground})`,
							backgroundSize: "cover",
							backgroundPosition: "center",
						}}
					/>
					<DotGrid
						dotSize={4}
						gap={15}
						baseColor="#3b0092"
						activeColor="#f09a38"
					/>
				</div>

				<PageHeaderHero
					titleAs="h1"
					title={t(`${base}.hero.title`)}
					description={t(`${base}.hero.description`)}
					meta={
						t(`${base}.hero.meta`, { returnObjects: true }) as string[]
					}
				/>
			</SectionWrapper>

			{/* OVERVIEW · KEY FEATURES · SPECIFICATIONS */}
			<AcheShowcase
				title={
					<>
						{t(`${base}.showcase.titleMain`)}{" "}
						<span
							className="italic font-medium"
							style={{ color: "#3b0092" }}>
							{t(`${base}.showcase.titleAccent`)}
						</span>
					</>
				}
				description={
					<>
						{descriptionParts[0]}
						<span
							className="font-semibold"
							style={{ color: "#3b0092" }}>
							{company}
						</span>
						{descriptionParts.slice(1).join(company)}
					</>
				}
				images={config.images}
				imageAlt={t(`${base}.showcase.imageAlt`)}
				wordmarkPrimary={config.wordmark}
				wordmarkSecondary="POSITRON"
				compliancePill={config.compliancePill}
				captionEyebrow={config.captionEyebrow}
				captionTitle={config.captionTitle}
				eyebrow={t(`${base}.showcase.eyebrow`)}
				specsEyebrow={t(`${base}.showcase.specsEyebrow`)}
				specsTitle={t(`${base}.showcase.specsTitle`)}
				highlights={highlights}
				specs={specs}
				specsFooterLabel={config.specsFooterLabel}
			/>

	

			<FooterSection />
		</>
	);
}
